import { Component, computed, DestroyRef, effect, inject, input, signal, untracked } from '@angular/core';

const START_PERCENTAGE = 2;
const TRICKLE_INTERVAL_MS = 250;
const SETTLE_WINDOW_MS = 200;
const HIDE_DELAY_MS = 500;

/** Slows down as it fills, so the bar never reaches 100% before the work actually settles. */
const nextIncrement = (percentage: number): number => {
  if (percentage < 25) {
    return Math.random() * 3 + 3;
  }
  if (percentage < 65) {
    return Math.random() * 3;
  }
  if (percentage < 90) {
    return Math.random() * 2;
  }
  if (percentage < 99) {
    return 0.5;
  }
  return 0;
};

@Component({
  selector: 'loading-bar',
  template: `
    @if (isVisible()) {
      <div
        aria-hidden="true"
        data-testid="loading-bar"
        [style.width.%]="percentage()"
        class="pointer-events-none fixed top-0 left-0 z-blocking h-2xs bg-brand transition-all duration-350"
      ></div>
    }
  `,
})
export class LoadingBarComponent {
  pendingRequestCount = input(0);

  isVisible = signal(false);
  percentage = signal(START_PERCENTAGE);

  #isLoading = computed(() => this.pendingRequestCount() > 0);

  #trickleId?: ReturnType<typeof setInterval>;
  #finishId?: ReturnType<typeof setTimeout>;

  constructor() {
    effect(() => {
      const isLoading = this.#isLoading();

      untracked(() => (isLoading ? this.#start() : this.#settle()));
    });

    inject(DestroyRef).onDestroy(() => this.#clearTimers());
  }

  #start(): void {
    this.#clearTimers();
    this.percentage.set(START_PERCENTAGE);
    this.isVisible.set(true);

    this.#trickleId = setInterval(() => {
      this.percentage.update((percentage) => percentage + nextIncrement(percentage));
    }, TRICKLE_INTERVAL_MS);
  }

  #settle(): void {
    this.#clearTimers();

    this.#finishId = setTimeout(() => {
      this.percentage.set(100);
      this.#finishId = setTimeout(() => this.isVisible.set(false), HIDE_DELAY_MS);
    }, SETTLE_WINDOW_MS);
  }

  #clearTimers(): void {
    clearInterval(this.#trickleId);
    clearTimeout(this.#finishId);
  }
}
